/**
 * Facets (fleet filter contract v3 §16.3, behind C86's lookup combobox): a lookup column's option counts are the rows
 * matching EVERY active filter EXCEPT its own column's, the search included, over the whole matching set (this app
 * loads the whole list, so that is every row, never a page). The semantics of tax-lifecycle's `facetCounts.ts` @
 * 7af60ebb (the C86 reference), adapted to this repo's `useListView`, which owns the search and the clock: the caller
 * passes the base rows (everything but this column's filter applied) and this file only tallies them.
 *
 * The "(has no value)" count rides in the same record under {@link NO_VALUE_TOKEN}, which is the key the copied
 * `SearchableMultiSelect` reads for that row.
 */
import { NO_VALUE_TOKEN, filterPrefix } from './columnFilterOps';
import { FilterColumn } from './types';

/** The record without this column's own `filter.<key>.*` entries. */
export function withoutColumn(filters: Record<string, string>, columnKey: string): Record<string, string> {
  const prefix = filterPrefix(columnKey);
  const others: Record<string, string> = {};
  for (const [k, v] of Object.entries(filters)) if (!k.startsWith(prefix)) others[k] = v;
  return others;
}

/** value → rows, plus `NO_VALUE_TOKEN` → rows with no value, for a picklist or boolean column. */
export function tallyLookup<T>(rows: readonly T[], column: FilterColumn<T>): Record<string, number> {
  const counts: Record<string, number> = { [NO_VALUE_TOKEN]: 0 };
  if (column.filterType !== 'picklist' && column.filterType !== 'boolean') return {};
  const get = column.getValue ?? (() => undefined);
  for (const row of rows) {
    const raw = get(row);
    let key: string | undefined;
    if (column.filterType === 'boolean') key = raw === true ? 'true' : raw === false ? 'false' : undefined;
    else key = raw === null || raw === undefined || raw === '' ? undefined : String(raw);
    const bucket = key ?? NO_VALUE_TOKEN;
    counts[bucket] = (counts[bucket] ?? 0) + 1;
  }
  return counts;
}
